import {
  Component,
  DestroyRef,
  inject,
  input,
  output,
  computed,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, debounceTime } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import {
  lucideSearch,
  lucidePlus,
  lucideMapPin,
  lucideCheck,
  lucideChevronDown,
} from '@ng-icons/lucide';
import { BrnPopoverImports } from '@spartan-ng/brain/popover';
import { BrnPopoverComponent } from '@spartan-ng/brain/popover';
import { BrnCommandImports } from '@spartan-ng/brain/command';
import { HlmLabelDirective } from '@spartan-ng/helm/label';
import { HlmPopoverImports } from '@spartan-ng/helm/popover';
import { HlmCommandImports } from '@spartan-ng/helm/command';
import { HlmIconDirective } from '@spartan-ng/helm/icon';
import { Address } from '@shared/models/address.model';

@Component({
  selector: 'app-address-selector',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NgIconComponent,
    HlmLabelDirective,
    HlmIconDirective,
    ...BrnPopoverImports,
    ...BrnCommandImports,
    ...HlmPopoverImports,
    ...HlmCommandImports,
  ],
  providers: [
    provideIcons({
      lucideSearch,
      lucidePlus,
      lucideMapPin,
      lucideCheck,
      lucideChevronDown,
    }),
  ],
  templateUrl: './address-selector.html',
  styleUrl: './address-selector.css',
})
export class AddressSelector {
  // ViewChild für Popover-Referenz
  @ViewChild(BrnPopoverComponent, { static: true })
  popover!: BrnPopoverComponent;

  // Inputs — data and loading state are owned by the parent smart component
  addresses = input<Address[]>([]);
  selectedAddressId = input<string | null>(null);
  placeholder = input<string>('Adresse auswählen...');
  disabled = input<boolean>(false);
  label = input<string>('Adresse');
  isLoading = input<boolean>(false);
  error = input<string | null>(null);
  /**
   * How many addresses the organization has in total. `addresses()` is only the
   * browsable page, so this is what tells the user the rest exist and are reachable
   * by typing. Leave undefined to hide the hint.
   */
  totalCount = input<number | undefined>(undefined);

  // Outputs
  addressSelected = output<Address>();
  createNewAddress = output<void>();
  /** Fired when the popover opens and data may need to be (lazily) fetched. */
  loadRequested = output<void>();
  /** Fired when the user requests a forced refresh of the address list. */
  refreshRequested = output<void>();
  /**
   * Debounced search term. The list is capped server-side, so filtering the loaded
   * page alone would never reach the rest — the parent re-queries the API with this.
   */
  searchChanged = output<string>();

  private readonly searchInput$ = new Subject<string>();

  constructor() {
    this.searchInput$
      .pipe(
        debounceTime(250),
        takeUntilDestroyed(inject(DestroyRef)),
      )
      .subscribe((term) => {
        this.searchPending.set(false);
        this.searchChanged.emit(term);
      });
  }

  private readonly lastSelection = signal<Address | null>(null);
  readonly searchTerm = signal('');
  private readonly searchPending = signal(false);
  readonly searching = computed(() => this.isLoading() || this.searchPending());

  /** Match the same address parts as the API without hiding valid reordered results. */
  readonly filterAddress = (value: string, search: string): boolean =>
    search.toLowerCase().split(/[\s,]+/).filter(Boolean)
      .every(part => value.toLowerCase().includes(part));

  onSearchInput(term: string): void {
    this.searchTerm.set(term.trim());
    this.searchPending.set(true);
    this.searchInput$.next(term.trim());
  }

  /** True when the loaded page hides rows that only a search will surface. */
  hasHiddenAddresses = computed(() => {
    const total = this.totalCount();
    return !this.searchTerm() && total !== undefined && total > this.addresses().length;
  });

  hiddenAddressCount = computed(() =>
    Math.max((this.totalCount() ?? 0) - this.addresses().length, 0),
  );

  selectedAddress = computed(() => {
    const addressId = this.selectedAddressId();
    return this.addresses().find((address) => address.addressId === addressId)
      ?? (this.lastSelection()?.addressId === addressId ? this.lastSelection() : null);
  });

  onPopoverOpenChange(isOpen: boolean): void {
    if (isOpen) {
      this.onSearchInput('');
      this.loadRequested.emit();
    }
  }

  selectAddress(address: Address): void {
    this.lastSelection.set(address);
    this.addressSelected.emit(address);
    this.popover.close();
  }

  onCreateNewAddress(): void {
    this.createNewAddress.emit();
    this.popover.close();
  }

  getAddressSearchValue(address: Address): string {
    return `${address.street} ${address.houseNumber} ${address.city} ${address.postalCode} ${address.country}`;
  }

  getAddressDisplay(address: Address): string {
    return `${address.street} ${address.houseNumber}, ${address.postalCode} ${address.city}`;
  }

  getSelectedAddressDisplay(): string {
    const address = this.selectedAddress();
    return address ? this.getAddressDisplay(address) : this.placeholder();
  }
}
